/**
 * Timezone Configuration
 * Centralized timezone data and types
 */

import { getTimezones as fetchTimezonesFromApi } from '@/services/siteService';
import type { TimezoneOption } from '@/services/siteService';

export type { TimezoneOption };

let cachedTimezones: TimezoneOption[] | null = null;

/**
 * Get full timezone options with all fields
 * @returns Promise of Array of TimezoneOption objects
 */
export const getFullTimezoneOptions = async (): Promise<TimezoneOption[]> => {
  if (cachedTimezones) {
    return cachedTimezones;
  }

  try {
    const timezones = await fetchTimezonesFromApi();
    cachedTimezones = timezones;
    return timezones;
  } catch (error) {
    console.error('Failed to fetch timezones:', error);
    return [];
  }
};

/**
 * Get timezone options formatted for dropdown components
 * @returns Promise of Array of { label, value } objects for use in dropdowns
 */
export const getTimezoneOptions = async (): Promise<Array<{ label: string; value: string }>> => {
  const timezones = await getFullTimezoneOptions();
  return timezones.map(tz => ({
    label: tz.label,
    value: tz.value,
  }));
};

/**
 * Get timezone abbreviation by timezone value
 * @param timezoneValue - IANA timezone string (e.g., "America/New_York")
 * @returns Timezone abbreviation (e.g., "EST") or undefined if not found
 */
export const getTimezoneAbbr = (timezoneValue: string): string | undefined => {
  if (!cachedTimezones) return undefined;
  const timezone = cachedTimezones.find(tz => tz.value === timezoneValue);
  return timezone?.abbr;
};

/**
 * Gets the user's local timezone from the browser
 * @returns IANA timezone string (e.g., "America/New_York", "Asia/Calcutta")
 * @description Returns the user's local timezone as detected by the browser.
 * Falls back to "UTC" if timezone detection is not available.
 */
export const getUserLocalTimezone = (): string => {
  if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Verify the timezone exists in our list if cached, otherwise return detected timezone
      if (cachedTimezones) {
        const exists = cachedTimezones.some(tz => tz.value === timeZone);
        return exists ? timeZone : 'UTC';
      }
      return timeZone;
    } catch (error) {
      console.warn('Failed to detect user timezone:', error);
      return 'UTC';
    }
  }
  return 'UTC';
};

